"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

// Hard-reload the home page (`/`) on every arrival after the initial mount.
// Reason: Next.js App Router cache restores the DOM but does NOT reliably
// re-attach React's synthetic event listeners, re-run useEffects, or
// re-initialize component-level state. Hard-reload on arrival guarantees a
// fresh mount every time so skip-intro and section animations all behave
// like a fresh visit.
//
// Other routes (`/pricing`, `/features`, etc.) keep normal SPA navigation —
// nothing on them needs a fresh mount.
//
// Also handles bfcache restores via `pageshow` with persisted=true.
export default function ReloadOnBack() {
  const pathname = usePathname();

  // Track the path we last SAW rather than "have we mounted yet".
  //
  // The previous implementation used an isFirstMount ref that it flipped to
  // false on the first effect run. Under React StrictMode — on by default in
  // dev for the App Router — effects run setup → cleanup → setup while the ref
  // survives, so the second setup saw isFirstMount === false and reloaded. On
  // "/" that reload produced a fresh mount, which did it again: an infinite
  // reload loop on the home page in development.
  //
  // Comparing against the previous pathname is immune to that. A repeated
  // setup for the same path is a no-op; only a genuine navigation to "/"
  // triggers the reload.
  const prevPath = useRef(pathname);

  useEffect(() => {
    if (prevPath.current === pathname) return;
    prevPath.current = pathname;
    if (pathname === "/") {
      window.location.reload();
    }
  }, [pathname]);

  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) window.location.reload();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  return null;
}
